import { getRumClientForIntegrations } from '@mint-lib/datadog';
import type { IFlagsmith } from '@mint-lib/flags';
import { FlagsmithProviders } from '@mint-lib/flags-envs';
import { PermissionsRequestType } from '@mint-lib/profile-helpers';
import type {
  DefaultError,
  FetchQueryOptions,
  QueryKey,
} from '@mint-lib/query-client';
import {
  ServiceLocator,
  type ServiceArgs,
  type ServiceInstance,
  type ServicesNames,
} from '@mint-lib/service-locator';

import { authFactory } from './factories/auth.js';
import { flagsmithEventsAbstractFactory } from './factories/flagsmith-events.js';
import { flagsmithAbstractFactory } from './factories/flagsmith.js';
import { httpFactory } from './factories/http.js';
import { i18nFactory } from './factories/i18n.js';
import { profileFactory } from './factories/profile.js';
import { queryClientFactory } from './factories/queryClient.js';
import { requestFactory } from './factories/request.js';
import type { MintServices, RouterContext } from './types.js';

export function makeRoutingContext(): RouterContext {
  const serviceLocator = new ServiceLocator<MintServices>();
  serviceLocator.registerAbstractFactory('auth', authFactory);
  serviceLocator.registerInstance('datadog', getRumClientForIntegrations());
  serviceLocator.registerAbstractFactory('profile', profileFactory);
  serviceLocator.registerAbstractFactory(
    'flagsmithEvents',
    flagsmithEventsAbstractFactory,
  );
  serviceLocator.registerAbstractFactory('flagsmith', flagsmithAbstractFactory);
  serviceLocator.registerAbstractFactory('i18n', i18nFactory);
  serviceLocator.registerAbstractFactory('http', httpFactory);
  serviceLocator.registerAbstractFactory('queryClient', queryClientFactory);
  serviceLocator.registerAbstractFactory('request', requestFactory);

  async function isFlagEnabled(
    env: keyof typeof FlagsmithProviders,
    flagName: string,
  ) {
    return (
      await serviceLocator.getOrThrowInstance('flagsmith', env)
    ).hasFeature(flagName);
  }

  async function isRedmillUser() {
    return (await serviceLocator.getOrThrowInstance('profile')).isRedmillUser();
  }

  async function isAuthenticated() {
    return (await serviceLocator.getOrThrowInstance('auth')).isLoggedIn();
  }

  async function getAuth0Credentials() {
    return (await serviceLocator.getOrThrowInstance('auth')).auth0Credentials;
  }

  async function hasPermission(permission: PermissionsRequestType) {
    return (await serviceLocator.getOrThrowInstance('profile')).hasPermission(
      permission,
    );
  }

  async function isControllerCompany() {
    return (
      await serviceLocator.getOrThrowInstance('profile')
    ).isControllerCompany();
  }

  async function isAgencyCompany() {
    return (
      await serviceLocator.getOrThrowInstance('profile')
    ).isAgencyCompany();
  }

  async function isRetailLicensee() {
    return (
      await serviceLocator.getOrThrowInstance('profile')
    ).isRetailLicensee();
  }

  async function getGuardContext(flagsService?: IFlagsmith) {
    return {
      profileService: await serviceLocator.getOrThrowInstance('profile'),
      authService: await serviceLocator.getOrThrowInstance('auth'),
      flagsService:
        flagsService ||
        (await serviceLocator.getOrThrowInstance('flagsmith', '@mint/app')),
    };
  }

  async function load<T>(
    url: string,
    params?: Record<string, string | number>,
    type: 'guestApi' | 'defaultApi' = 'defaultApi',
  ): Promise<T> {
    return serviceLocator.getOrThrowInstance('request', url, params, type) as T;
  }

  async function prefetchQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = never,
  >(
    queryOptions: FetchQueryOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParam
    >,
  ) {
    const queryClient = await serviceLocator.getOrThrowInstance('queryClient');

    return queryClient.fetchQuery(queryOptions);
  }

  async function getInstance<
    Key extends ServicesNames<MintServices>,
    Args extends ServiceArgs<MintServices, Key>,
    Instance extends ServiceInstance<MintServices, Key, Args>,
  >(name: Key, ...args: Args): Promise<Instance> {
    return serviceLocator.getOrThrowInstance<Key, Args, Instance>(
      name,
      ...args,
    );
  }

  return {
    isFlagEnabled,
    isRedmillUser,
    isAuthenticated,
    hasPermission,
    getInstance,
    getGuardContext,
    getAuth0Credentials,
    load,
    prefetchQuery,
    serviceLocator,
    profileHelpers: {
      isControllerCompany,
      isAgencyCompany,
      isRetailLicensee,
    },
  };
}
